// Library methods
import { useCallback, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth0 } from "@auth0/auth0-react";

// Components
import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import Toolbar from "@mui/material/Toolbar";
import NavbarTextButton from "./NavbarTextButton/NavbarTextButton";
import NavbarMobileMenu from "./NavbarMobileMenu/NavbarMobileMenu";
import NavbarUserMenu from "./NavbarUserMenu/NavbarUserMenu";
import NavbarIconButton from "./NavbarIconButton/NavbarIconButton";
import SignOutModal from "./SignOutModal/SignOutModal";
import ClinicSwitcher from "./ClinicSwitcher/ClinicSwitcher";
import DeviceVersionBanner from "./DeviceVersionBanner/DeviceVersionBanner";
import LiveUpdateBanner from "./LiveUpdateBanner/LiveUpdateBanner";
import HubProblemBanner from "./HubProblemBanner/HubProblemBanner";
import {
  GearIcon,
  LightbulbIcon,
  LogoutIcon,
  MoonIcon,
  QuestionCircleIcon,
  SunIcon,
} from "src/assets/icons";
import eyeLogo from "src/assets/images/logos/eyelogo.svg";

// Contexts
import { useClinicContext } from "src/contexts/clinic/ClinicProvider";
import { useThemeModeContext } from "src/contexts/theme/ThemeModeContext";

// Hooks
import { useEMRHubs } from "src/services/emr/hooks/emr-hubs/useEMRHubs";

type NavbarType = {
  canNavigate?: boolean;
};

const Navbar = ({ canNavigate = true }: NavbarType) => {
  const { t } = useTranslation();
  const { logout } = useAuth0();
  const location = useLocation();
  const navigate = useNavigate();

  const [signOutModal, setSignOutModal] = useState(false);

  const { clinicInfo, isLoading } = useClinicContext();
  const { mode, toggleMode } = useThemeModeContext();

  const canUseEMR = useMemo(
    () => clinicInfo?.licensing?.canUseEMR && !isLoading,
    [clinicInfo?.licensing?.canUseEMR, isLoading]
  );

  const { data: emrHubs, isLoading: isLoadingHubs } = useEMRHubs(
    clinicInfo?.id
  );

  // Don't display banners on the report pages
  const canDisplayBanners = useMemo(
    () =>
      !location.pathname.includes("exam") &&
      !location.pathname.includes("progression-analysis"),
    [location.pathname]
  );

  // Array check, not just length: a misrouted request can come back as the
  // portal's index.html, which has a length but no .map
  const canDisplayEMRBanner = useMemo(
    () =>
      Boolean(
        canUseEMR &&
          !isLoadingHubs &&
          Array.isArray(emrHubs) &&
          emrHubs.length > 0
      ),
    [canUseEMR, emrHubs, isLoadingHubs]
  );

  const handleSignOutConfirm = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      if (event) {
        // Clear local storage
        localStorage.clear();
        sessionStorage.clear();
        logout({
          logoutParams: {
            returnTo: process.env.REACT_APP_URL,
            client_id: process.env.REACT_APP_AUTH0_CLIENT_ID,
          },
        });
      }
      // Close modal
      setSignOutModal(false);
    },
    [logout]
  );

  const onClickLogo = useCallback(
    () => (canNavigate ? navigate("/dashboard") : null),
    [canNavigate, navigate]
  );

  const onClickHelp = useCallback(() => {
    navigate("/knowledge-hub/operating-manual");
  }, [navigate]);

  // all possible routes on the admin navbar
  const navbarPages = [
    {
      dataTestId: "navbar-dashboard-button",
      title: t("Dashboard"),
      path: "/dashboard",
      showTab: true,
    },
    {
      dataTestId: "navbar-patients-button",
      title: t("Patients"),
      path: "/patients",
      showTab: true,
    },
    {
      dataTestId: "navbar-emr-button",
      title: t("Data Hub"),
      path: "/data-hub",
      showTab: canUseEMR,
    },
  ];

  // all options on the user dropdown menu
  const menuOptions = [
    {
      icon: <GearIcon />,
      title: t("Settings"),
      onClick: () => navigate("/settings"),
      showOption: canNavigate,
    },
    {
      icon: <LightbulbIcon />,
      title: t("Knowledge Hub"),
      onClick: () => navigate("/knowledge-hub/tutorial-videos"),
      showOption: canNavigate,
    },
    {
      icon: <LogoutIcon />,
      title: t("Sign Out"),
      onClick: () => setSignOutModal(true),
      showOption: true,
    },
  ];

  return (
    <>
      <Box id="navbar-container" zIndex={99}>
        <AppBar
          position="static"
          elevation={0}
          sx={{
            backgroundColor: "#202338",
            color: "#FFF",
            height: { xs: 42, sm: 85 },
            display: "flex",
            justifyContent: "center",
            padding: "24px",
          }}
        >
          <Toolbar disableGutters>
            <Box
              component="img"
              sx={{
                flexGrow: 1,
                maxWidth: { xs: 47, sm: 73 },
                display: "flex",
                cursor: canNavigate ? "pointer" : "default",
                marginRight: "24px",
              }}
              alt="RetinaLogik Logo"
              src={eyeLogo}
              draggable={false}
              onClick={onClickLogo}
            />
            <Box
              sx={{
                flexGrow: 1,
                display: { xs: "none", sm: "flex" },
                gap: "24px",
                // lets the tabs shrink below their text width so titles can ellipsize
                minWidth: 0,
              }}
            >
              {canNavigate &&
                navbarPages.map((navbarPage) => (
                  <NavbarTextButton
                    key={navbarPage.title}
                    dataTestId={navbarPage.dataTestId}
                    title={navbarPage.title}
                    onClick={() => navigate(navbarPage.path)}
                    isUnderlined={location.pathname.startsWith(navbarPage.path)}
                    showTab={navbarPage.showTab}
                  />
                ))}
            </Box>
            <Box
              style={{
                display: "flex",
                alignItems: "center",
                flexGrow: 1,
                justifyContent: "flex-end",
              }}
            >
              <Box
                display="flex"
                alignItems="center"
                gap={{ xs: "0px", sm: "24px" }}
              >
                {/* Toggles light/dark theme mode */}
                <NavbarIconButton
                  dataTestId="navbar-theme-mode-button"
                  title=""
                  onClick={toggleMode}
                  isUnderlined={false}
                  icon={mode === "dark" ? <SunIcon /> : <MoonIcon />}
                  iconOnly
                />

                {/* Allows clinic to be changed from the navbar */}
                <ClinicSwitcher />

                {/* Displays Help button for the operating manual */}
                {canNavigate && (
                  <NavbarIconButton
                    dataTestId="navbar-help-button"
                    title={t("Help")}
                    onClick={onClickHelp}
                    isUnderlined={location.pathname.startsWith(
                      "/knowledge-hub/operating-manual"
                    )}
                    icon={<QuestionCircleIcon />}
                  />
                )}

                {/* Displays user actions (settings, logout, etc) */}
                <NavbarUserMenu
                  menuOptions={menuOptions.filter(
                    (option) => option.showOption
                  )}
                />

                <Box
                  sx={{
                    display: { xs: canNavigate ? "flex" : "none", sm: "none" },
                  }}
                >
                  {/* Displays navigation/route options on mobile */}
                  <NavbarMobileMenu navbarPages={navbarPages} />
                </Box>
              </Box>
            </Box>
          </Toolbar>
        </AppBar>
        {canDisplayBanners && (
          <>
            <DeviceVersionBanner />
            {canDisplayEMRBanner && emrHubs && (
              <HubProblemBanner emrHubs={emrHubs} />
            )}
            <LiveUpdateBanner />
          </>
        )}
      </Box>
      <SignOutModal
        open={signOutModal}
        onClose={() => setSignOutModal(false)}
        onConfirm={handleSignOutConfirm}
      />
    </>
  );
};

export default Navbar;
